import React, { useState, useEffect } from 'react';
import {
  createTheme,
  ThemeProvider,
  CssBaseline,
  Box,
  Container,
  Paper,
  Stack,
  Typography,
  Chip,
  Divider,
  FormControlLabel,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
} from '@mui/material';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    background: { default: '#0c0d12', paper: '#14171f' },
    text: {
      primary: '#e8e9ee',
      secondary: 'rgba(232,233,238,0.7)',
    },
    primary: { main: '#6aa9ff' },
  },
  typography: {
    fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
  },
});

const STORAGE_KEYS = {
  showSender: 'smsReceiveShowSender',
  showReceiver: 'smsReceiveShowReceiver',
};

const DataTable = () => {
  const [smsData, setSmsData] = useState([]);
  const [timeLeft, setTimeLeft] = useState(15);
  const [showSender, setShowSender] = useState(() => {
    const savedValue = localStorage.getItem(STORAGE_KEYS.showSender);
    return savedValue === null ? true : savedValue === 'true';
  });
  const [showReceiver, setShowReceiver] = useState(() => {
    const savedValue = localStorage.getItem(STORAGE_KEYS.showReceiver);
    return savedValue === null ? true : savedValue === 'true';
  });
  const isMobile = useMediaQuery(darkTheme.breakpoints.down('sm'));
  const visibleRows = smsData.slice(0, 10000);
  const portWidthCh = Math.max(
    4,
    ...visibleRows.map((sms) => String(sms.port ?? '-').length)
  );
  const senderWidthCh = Math.max(
    8,
    ...visibleRows.map((sms) => String(sms.sender ?? '-').length)
  );
  const receiverWidthCh = Math.max(
    8,
    ...visibleRows.map((sms) => String(sms.receiver ?? '-').length)
  );
  const mobilePortWidth = `${portWidthCh + 1}ch`;
  const mobileSenderWidth = `${Math.min(senderWidthCh + 1, 14)}ch`;
  const mobileReceiverWidth = `${Math.min(receiverWidthCh + 1, 14)}ch`;
  const mobileVisibleAuxColumns =
    1 + (showSender ? 1 : 0) + (showReceiver ? 1 : 0);
  const mobileReservedWidth =
    portWidthCh +
    (showSender ? Math.min(senderWidthCh + 1, 14) : 0) +
    (showReceiver ? Math.min(receiverWidthCh + 1, 14) : 0) +
    mobileVisibleAuxColumns * 2;

  useEffect(() => {
    const fetchData = () => {
      fetch('https://frek.cc/proxy/38.54.86.24:5005/get-sms')
        .then((response) => response.json())
        .then((data) => {
          const filteredData = data.filter(
            (sms) => !sms.content.includes('(02)') && !sms.content.includes('(03)')
          );
          const sortedData = filteredData.sort((a, b) => new Date(b.date) - new Date(a.date));
          setSmsData(sortedData);
        })
        .catch((error) => console.error('Error fetching SMS data:', error));
    };

    fetchData();
    const intervalId = setInterval(() => {
      fetchData();
      setTimeLeft(15);
    }, 15000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const timerId = setInterval(() => {
      setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(timerId);
  }, []);

  useEffect(() => {
    document.title = 'SMS RECEIVE Logs';
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.showSender, String(showSender));
  }, [showSender]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.showReceiver, String(showReceiver));
  }, [showReceiver]);

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          background:
            'radial-gradient(1200px 600px at 15% -10%, rgba(78, 118, 255, 0.28), transparent 60%),' +
            'radial-gradient(900px 600px at 110% 10%, rgba(0, 200, 170, 0.22), transparent 55%),' +
            'linear-gradient(180deg, #0b0b10 0%, #101018 45%, #0d0f12 100%)',
          color: '#e8e9ee',
          py: { xs: 1.5, sm: 2 },
        }}
      >
        <Container maxWidth={false} sx={{ px: { xs: 1.25, sm: 2, md: 3 } }}>
          <Stack spacing={{ xs: 1.25, sm: 1.5 }}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 1.5, sm: 2 },
                borderRadius: { xs: 2, sm: 3 },
                background: 'rgba(18, 18, 22, 0.75)',
                border: '1px solid rgba(255,255,255,0.06)',
                backdropFilter: 'blur(6px)',
              }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.2}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
              >
                <Typography
                  variant={isMobile ? 'h6' : 'h5'}
                  sx={{ fontWeight: 700, lineHeight: 1.1 }}
                >
                  SMS Received Realtime Logs
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    label={`${smsData.length} messages`}
                    size={isMobile ? 'small' : 'medium'}
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.08)',
                      color: '#e8e9ee',
                      fontWeight: 700,
                    }}
                  />
                  <Chip
                    label={`Next Fetch In ${timeLeft}s`}
                    size={isMobile ? 'small' : 'medium'}
                    sx={{
                      width: 'fit-content',
                      bgcolor: 'rgba(106,169,255,0.2)',
                      color: '#d5e7ff',
                      fontWeight: 700,
                    }}
                  />
                </Stack>
              </Stack>
            </Paper>

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

            <Paper
              elevation={0}
              sx={{
                borderRadius: { xs: 2, sm: 3 },
                background: 'rgba(18, 18, 22, 0.75)',
                border: '1px solid rgba(255,255,255,0.06)',
                overflow: 'hidden',
              }}
            >
              {isMobile && (
                <Stack
                  direction="row"
                  spacing={0.5}
                  sx={{
                    px: 1,
                    py: 0.75,
                    borderBottom: '1px solid rgba(255,255,255,0.08)',
                    backgroundColor: 'rgba(255,255,255,0.02)',
                  }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showSender}
                        onChange={(event) => setShowSender(event.target.checked)}
                        size="small"
                      />
                    }
                    label="Sender"
                    sx={{
                      m: 0,
                      '.MuiFormControlLabel-label': { fontSize: '0.72rem' },
                    }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showReceiver}
                        onChange={(event) => setShowReceiver(event.target.checked)}
                        size="small"
                      />
                    }
                    label="Receiver"
                    sx={{
                      m: 0,
                      '.MuiFormControlLabel-label': { fontSize: '0.72rem' },
                    }}
                  />
                </Stack>
              )}
              <TableContainer
                sx={{
                  maxHeight: '78vh',
                    overflowX: { xs: 'auto', sm: 'hidden' },
                  '&::-webkit-scrollbar': {
                    height: 8,
                    width: 8,
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(255,255,255,0.16)',
                    borderRadius: 999,
                  },
                }}
              >
                <Table
                  size="small"
                  stickyHeader
                  sx={{
                    width: { xs: 'fit-content', sm: '100%' },
                    minWidth: { xs: '100%', sm: '100%' },
                    maxWidth: 'none',
                    tableLayout: { xs: 'auto', sm: 'fixed' },
                  }}
                >
                  <colgroup>
                    <col style={{ width: isMobile ? mobilePortWidth : '5%' }} />
                    {(showSender || !isMobile) && (
                      <col style={{ width: isMobile ? mobileSenderWidth : '11%' }} />
                    )}
                    {(showReceiver || !isMobile) && (
                      <col style={{ width: isMobile ? mobileReceiverWidth : '11%' }} />
                    )}
                    <col
                      style={{
                        width: isMobile
                          ? `max(12rem, calc(100vw - ${mobileReservedWidth}ch))`
                          : '73%',
                      }}
                    />
                  </colgroup>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          fontSize: { xs: '0.72rem', sm: '0.8rem' },
                          py: { xs: 1, sm: 1.25 },
                          px: { xs: 0.5, sm: 1.5 },
                          color: '#d9e6ff',
                          backgroundColor: 'rgba(30, 35, 52, 0.92)',
                          borderBottom: '1px solid rgba(255,255,255,0.12)',
                          textAlign: 'center',
                        }}
                      >
                        Port
                      </TableCell>
                      {(showSender || !isMobile) && (
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            fontSize: { xs: '0.72rem', sm: '0.8rem' },
                            py: { xs: 1, sm: 1.25 },
                            px: { xs: 0.5, sm: 1.5 },
                            color: '#d9e6ff',
                            backgroundColor: 'rgba(30, 35, 52, 0.92)',
                            borderBottom: '1px solid rgba(255,255,255,0.12)',
                            textAlign: 'center',
                          }}
                        >
                          Sender
                        </TableCell>
                      )}
                      {(showReceiver || !isMobile) && (
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            fontSize: { xs: '0.72rem', sm: '0.8rem' },
                            py: { xs: 1, sm: 1.25 },
                            px: { xs: 0.5, sm: 1.5 },
                            color: '#d9e6ff',
                            backgroundColor: 'rgba(30, 35, 52, 0.92)',
                            borderBottom: '1px solid rgba(255,255,255,0.12)',
                            textAlign: 'center',
                          }}
                        >
                          Receiver
                        </TableCell>
                      )}
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          fontSize: { xs: '0.72rem', sm: '0.8rem' },
                          py: { xs: 1, sm: 1.25 },
                          px: { xs: 0.5, sm: 1.5 },
                          color: '#d9e6ff',
                          backgroundColor: 'rgba(30, 35, 52, 0.92)',
                          borderBottom: '1px solid rgba(255,255,255,0.12)',
                          textAlign: 'center',
                        }}
                      >
                        Content
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleRows.map((sms, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          '& td': { borderColor: 'rgba(255,255,255,0.08)' },
                          '&:hover': { backgroundColor: 'rgba(255,255,255,0.03)' },
                        }}
                      >
                        <TableCell
                          align="center"
                          sx={{
                            fontSize: { xs: '0.72rem', sm: '0.8rem' },
                            py: { xs: 0.85, sm: 1.1 },
                            px: { xs: 0.35, sm: 1.1 },
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {sms.port}
                        </TableCell>
                        {(showSender || !isMobile) && (
                          <TableCell
                            align="center"
                            sx={{
                              fontSize: { xs: '0.72rem', sm: '0.8rem' },
                              py: { xs: 0.85, sm: 1.1 },
                              px: { xs: 0.35, sm: 1.1 },
                              whiteSpace: { xs: 'normal', sm: 'normal' },
                              wordBreak: 'break-word',
                              lineHeight: 1.2,
                            }}
                          >
                            {sms.sender}
                          </TableCell>
                        )}
                        {(showReceiver || !isMobile) && (
                          <TableCell
                            align="center"
                            sx={{
                              fontSize: { xs: '0.72rem', sm: '0.8rem' },
                              py: { xs: 0.85, sm: 1.1 },
                              px: { xs: 0.35, sm: 1.1 },
                              whiteSpace: { xs: 'normal', sm: 'normal' },
                              wordBreak: 'break-word',
                              lineHeight: 1.2,
                            }}
                          >
                            {sms.receiver}
                          </TableCell>
                        )}
                        <TableCell
                          align="center"
                          sx={{
                            fontSize: { xs: '0.72rem', sm: '0.8rem' },
                            py: { xs: 0.85, sm: 1.1 },
                            px: { xs: 0.5, sm: 1.5 },
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            overflowWrap: 'anywhere',
                            minWidth: { xs: '12rem', sm: 'auto' },
                            maxWidth: { xs: 'calc(100vw - 7rem)', sm: 'none' },
                          }}
                        >
                          {sms.content}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Stack>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default DataTable;
